/**
 * A hosted repo's settings, for the Repos view: the commands it is built,
 * committed and deployed with, and the domains it is served on.
 *
 * One set per repo, server-wide — a repo is a property of the box, not of a
 * team — stored as JSON (`repo_settings`) and re-parsed on every read, so a
 * field added later needs no migration. A repo nobody has saved gets
 * `DEFAULT_REPO_SETTINGS`.
 */
import { branchNameProblem, worktreeNameProblem } from "./types.js";
import { isStartMode, type StartMode } from "./startMode.js";
import { TASK_PROMPT_MAX, fillTemplate, namesTag } from "./taskPrompt.js";

export interface RepoCommand {
  /** Stable across edits, so a run names the command and not its position. */
  id: string;
  label: string;
  /** A bash command line, run in the claude user's checkout of the repo. */
  command: string;
}

export interface RepoSettings {
  commands: RepoCommand[];
  /** Host names, optionally with a port: `todos.example.com`, never a URL. */
  domains: string[];
  /** Run Deploy whenever main moves past what is live (server/autoDeploy.ts).
   *  Absent is off: only a repo someone turned it on for deploys unasked.
   *  The trunk's `RepoBranch.autoDeploy` once the branches are written: read,
   *  never written — `withAutos` sets it. */
  autoDeploy?: boolean;
  /** The repo's branches and the merges between them (`branchesOf`): each
   *  branch's auto-push, and each direction of a link's auto-merge. Absent is
   *  what the flags below said before there were branches (`legacyBranches`);
   *  written, the flags below are DERIVED from it by the parser. */
  branches?: RepoBranch[];
  merges?: RepoMerge[];
  /** Run Deploy whenever staging has commits main lacks: the filled Deploy
   *  merges staging into main first (server/autoDeploy.ts). Absent is off.
   *  The staging → main link's auto (`branchesOf`): read, never written. */
  autoDeployStaging?: boolean;
  /** Merge a Claude worktree's branch into its target (staging, unless the
   *  worktrees' link names another) whenever a commit lands on it
   *  (server/autoMerge.ts): when it was turned on, in ms, so work already
   *  sitting unmerged in a worktree is not swept in by ticking the box. Absent is off.
   *  The worktrees' link's auto (`branchesOf`): read, never written. */
  autoMerge?: number;
  /** Commit a Claude worktree's changes, its task's title the message, once
   *  its Claude stops at its prompt (server/autoCommit.ts). Absent is off. */
  autoCommit?: boolean;
  /** Some branch is pushed to the git server on its own (server/autoPush.ts,
   *  `RepoBranch.autoPush`): read, never written. Absent is off. */
  autoPush?: boolean;
  /** Some branch is pulled from the git server on its own (server/autoPush.ts,
   *  `RepoBranch.autoPull`): read, never written. Absent is off. */
  autoPull?: boolean;
  /** How auto-fix starts a Claude — after a failed Deploy, or on a merge that
   *  conflicted (server/mergeFix.ts `autoFixMay`): Manual is off, else the
   *  repo's start limit (`startLimit`, Ready while there is none). DERIVED by
   *  the parser from auto-fix being on and the limit: set it with `withAutoFix`.
   *  Saved before the limit, Under or Wastage here IS the limit. */
  autoFixMode?: StartMode;
  /** Auto-fix is on (`autoFixMode` not Manual): read, never written — kept so the
   *  build before start modes reads sense. */
  autoFix?: boolean;
  /** The repo's start limit: every queued Claude and queued prompt in the repo,
   *  and auto-fix, goes no more readily than this (`startModeOf`'s `repoMode`) —
   *  Under: only while the plan usage is under its pace (or in wastage); Wastage:
   *  only in wastage. Absent is no limit (Ready). */
  startLimit?: RepoStartLimit;
  /** At most this many Claudes at work in the repo at once (`busyClaudes()` with
   *  the repo's `repo`), over and above the Global settings' Parallel Claudes:
   *  server/tmux.ts refuses a start past it, and a queued one stays queued.
   *  Absent is no limit. Auto-fix is exempt, as from the global one. */
  maxClaudes?: number;
  /** Set by a failed Deploy that started auto-fix: the fix's worktree, and
   *  which of the two auto-deploy boxes the failure unticked (maybe neither).
   *  Once that worktree is merged into staging they are ticked again
   *  (server/autoDeploy.ts `fixMerged`). Either box set by hand drops it.
   *  While it is set and auto-fix is on, auto-merge holds every other
   *  worktree back (`fixingWorktree`). */
  autoDeployResume?: AutoDeployResume;
  /** "Start at review" was off on every branch (`RepoBranch.startAtReview`): read,
   *  never written — kept so a rolled-back build reads sense. Before the branches
   *  carried it, the repo's one box: then every branch is off. */
  startAtReview?: boolean;
  /** The filled Deploy passes `--quick` to the box's deploy script, which then
   *  skips the typecheck and tests and reuses node_modules — only a script that
   *  says it takes it (`QUICK_DEPLOY_MARK`). Absent is off. */
  quickDeploy?: boolean;
  /** Worktrees starred from their pill's menu, by name: drawn with a star, always
   *  shown in the git panel (listed above the log when their commit is not in it),
   *  and never removed on their own — not by `worktreePrune`, nor by their task
   *  going done. Only the pill's Delete takes one. Also old worktrees, starred
   *  from their `worktree/…` tag's menu, by the tag's full name: shown even with
   *  the panel's "old worktrees" box unticked. Absent is none. */
  starred?: string[];
  /** What Claude is told in this repo instead of the server-wide prompts
   *  (`REPO_PROMPTS`), each absent while the default stands. Edited with the
   *  commands (the whole-set PUT). */
  prompts?: RepoPrompts;
  /** Shell commands run as a worktree comes and goes (`WORKTREE_HOOKS`), each
   *  absent while unset. Edited with the commands (the whole-set PUT). */
  worktreeHooks?: WorktreeHooks;
  /** What a branch's auto-test runs (`RepoBranch.test`, server/branchRuns.ts),
   *  in a throwaway tree at the branch's tip after the create hook. Absent is
   *  `DEFAULT_TEST_COMMAND`. Edited with the commands (the whole-set PUT). */
  testCommand?: string;
}

/** What a branch's auto-test runs when the repo names nothing else. */
export const DEFAULT_TEST_COMMAND = "npm test";

/** The repo's test command, or the default. */
export const testCommandOf = (s: RepoSettings | undefined): string => s?.testCommand || DEFAULT_TEST_COMMAND;

/**
 * A repo's worktree hooks: bash, run as `claude` IN the worktree, with
 * `$WORKTREE` its name and `$CHECKOUT` the checkout it hangs off — so
 * `ln -s "$CHECKOUT/node_modules" .` or `cp "$CHECKOUT/.env" .` just work.
 */
export interface WorktreeHooks {
  /** Once the bridge has made a worktree (runner/tmux.ts), before its Claude
   *  starts: in that Claude's session, so its output is there to read. A
   *  failure is reported and the Claude starts anyway. */
  create?: string;
  /** Before a worktree is removed — Delete, a kill's "remove its worktree",
   *  done's clean-up and the prune (`withRemoveHook`). A failure is reported
   *  and the removal goes on. */
  remove?: string;
}

export type WorktreeHookKey = keyof WorktreeHooks;

/** Each hook, for the Repos view's editor. */
export const WORKTREE_HOOKS: { key: WorktreeHookKey; label: string; hint: string; placeholder: string }[] = [
  {
    key: "create",
    label: "On create",
    hint: "Run in a new worktree before its Claude starts — $WORKTREE is its name, $CHECKOUT the checkout's path. Blank runs nothing.",
    placeholder: "npm ci",
  },
  {
    key: "remove",
    label: "On remove",
    hint: "Run in a worktree just before it is removed, with the same variables. Blank runs nothing.",
    placeholder: "docker compose down",
  },
];

/** The repo's hook `key`, or null while it has none. */
export const worktreeHook = (s: RepoSettings | undefined, key: WorktreeHookKey): string | null => s?.worktreeHooks?.[key] || null;

/**
 * A worktree-removing command line with the repo's remove hook run first, in
 * the worktree (the run route's cwd), in a subshell so an `exit` in it cannot
 * end the removal. No hook, the command as it was.
 */
export function withRemoveHook(command: string, hook: string | null): string {
  if (!hook) return command;
  return (
    '( export WORKTREE=$(basename "$PWD") CHECKOUT=$(git worktree list --porcelain | sed -n "1s/^worktree //p"); ' +
    `${hook}
) || echo "The worktree's remove hook failed ($?)" >&2; ${command}`
  );
}

/** A repo's own prompts, each in place of the server-wide one. */
export interface RepoPrompts {
  /** The brief's template (shared/taskPrompt.ts) for a task in this repo, in
   *  place of the Global settings one: the same tags. */
  task?: string;
  /** What auto-fix (and a pill's Test) asks its Claude, in place of
   *  `DEFAULT_AUTO_FIX_PROMPT`: a template, `<failed>` the Deploy's output
   *  (`autoFixPrompt()`); one naming no tag gets it after it. */
  autoFix?: string;
  /** What auto-merge types at a worktree's Claude when its branch conflicts
   *  with staging, in place of `AUTO_MERGE_CONFLICT_PROMPT`. */
  mergeConflict?: string;
}

export type RepoPromptKey = keyof RepoPrompts;
export const REPO_PROMPT_MAX = TASK_PROMPT_MAX;

/** Each prompt a repo may override, for the Repos view's editor. */
export const REPO_PROMPTS: { key: RepoPromptKey; label: string; hint: string }[] = [
  { key: "task", label: "Task brief", hint: "The template a task's brief for Claude is filled from — the same tags, and [brackets], as Global settings' task prompt. Blank uses that one." },
  { key: "autoFix", label: "Auto-fix", hint: "What a Claude started by auto-fix or a pill's Test is asked — the same tags and [brackets] as the task brief: <failed> is what failed in the Deploy, empty for a Test. Without it, what failed follows the prompt. Blank uses the default." },
  { key: "mergeConflict", label: "Merge conflict", hint: "What a worktree's Claude is asked when merging its branch with staging conflicts. Blank uses the default." },
];

/** The repo's own prompt `key`, or null while it uses the server-wide one. */
export const repoPrompt = (s: RepoSettings | undefined, key: RepoPromptKey): string | null => s?.prompts?.[key] || null;

/** Whether worktree `name` is starred in these settings. */
export const isStarred = (s: RepoSettings | undefined, name: string | null | undefined): boolean => !!name && !!s?.starred?.includes(name);

/** Whether an old worktree's tag is starred: by its own name, or because its
 *  worktree was starred when it was deleted (the star goes with the work). */
export const isStarredTag = (s: RepoSettings | undefined, tag: string): boolean =>
  !!s?.starred?.includes(tag) || isStarred(s, worktreeOfTag(tag));

/** Whether `name` may be starred: a worktree's name, or an old worktree's tag. */
export const starrable = (name: string): boolean => !worktreeNameProblem(name) || worktreeOfTag(name) !== null;

/** Whether a queued Claude (or prompt) going off `branch` — the branch its new
 *  worktree is made at, a prompt's the one its worktree goes into — counts a
 *  dependency finished at `review`, or only at `done` (`RepoBranch.startAtReview`,
 *  absent is on). A branch the settings do not list goes as staging does. */
export function startsAtReview(s: RepoSettings | undefined, branch: string = STAGING_BRANCH): boolean {
  const { branches } = branchesOf(s);
  const b = branches.find((x) => x.name === branch) ?? branches.find((x) => x.name === STAGING_BRANCH);
  return b?.startAtReview !== false;
}

/** The auto-fix worktree a failed Deploy is waiting on, while auto-fix is on:
 *  the deploy chip says "Fixing", and auto-merge takes only that worktree into
 *  staging until it is in (or found nothing to fix). */
export const fixingWorktree = (s: RepoSettings | undefined): string | null => (s?.autoFix && s.autoDeployResume?.worktree) || null;

export interface AutoDeployResume {
  worktree: string;
  /** `autoDeploy` was on. */
  main: boolean;
  /** `autoDeployStaging` was on. */
  staging: boolean;
}

export interface RepoInfo {
  name: string;
  /** The git folder it is hosted in (shared/repoFolders.ts). Absent from an older server. */
  folder?: string;
  /** The teams whose boards list it: its folder is one of theirs. Absent from an older server. */
  teams?: string[];
  settings: RepoSettings;
  /** False while the settings are still the defaults nobody has saved. */
  saved: boolean;
  /** Whether the box's deploy script takes `--quick` (`QUICK_DEPLOY_MARK`). */
  quickDeployable?: boolean;
  /** The branches other than the trunk that have a deploy script of their own
   *  (`deploy-<repo>-<branchScriptSlug>`), by slug. Absent from an older bridge. */
  branchDeploys?: string[];
  /** Output tokens Claude Code has written in the repo, over every conversation
   *  on disk filed under its checkout or any worktree of it, gone or not: the
   *  git panel's pills and tags, summed and more. Absent from an older bridge. */
  claudeTokens?: number;
}

/** A line a deploy script carries when it takes `--quick`: the bridge reads it
 *  (runner/tmux.ts `deployScripts`), since sudo refuses an argument its rule
 *  does not name and a failed Deploy turns auto-deploy off. */
export const QUICK_DEPLOY_MARK = /^# todos: quick-deploy$/m;

/** The run route's id for one Deploy with `--quick`, whatever the box says (the
 *  main pill's "Quick deploy"). It runs as the Deploy — `id` "deploy", so it
 *  holds the same lock — only the command line and label differ (`quickDeployOf`). */
export const QUICK_DEPLOY_ID = "quick-deploy";

/** The Deploy as it runs with `--quick`, from the plain one filled from `script`;
 *  null when that Deploy is somebody's own line, not the script's. */
export function quickDeployOf(settings: RepoSettings, script: string): RepoCommand | null {
  const cmd = withDeployScript({ ...settings, quickDeploy: true }, script, true).commands.find((c) => c.id === "deploy");
  return cmd?.command === deployCommand(`${script} --quick`) ? { ...cmd, label: "Quick deploy" } : null;
}

export const REPO_COMMANDS_MAX = 30;
export const REPO_COMMAND_LABEL_MAX = 40;
export const REPO_COMMAND_MAX = 4000;
export const REPO_DOMAINS_MAX = 50;
export const RUN_MESSAGE_MAX = 2000;
export const STARRED_MAX = 200;

export const DEFAULT_REPO_SETTINGS: RepoSettings = {
  commands: [
    { id: "build", label: "Build", command: "npm run build" },
    { id: "commit", label: "Commit", command: 'git add -A && git commit -m "$MESSAGE"' },
    { id: "push", label: "Push", command: "git push" },
    { id: "deploy", label: "Deploy", command: "" },
  ],
  domains: [],
};

/** What Commit ran before Push was a button of its own. */
const OLD_COMMIT = 'git add -A && git commit -m "$MESSAGE" && git push';

/**
 * Settings saved while Commit also pushed, with that Commit split into Commit
 * and Push as the defaults now have them. Only the untouched old default is
 * split: a Commit somebody rewrote is theirs, and so is a list that already
 * has a Push.
 */
export function withPushSplit(settings: RepoSettings): RepoSettings {
  const at = settings.commands.findIndex((c) => c.id === "commit" && c.command === OLD_COMMIT);
  if (at < 0 || settings.commands.some((c) => c.id === "push")) return settings;
  const commands = [...settings.commands];
  commands.splice(at, 1, { ...commands[at]!, command: 'git add -A && git commit -m "$MESSAGE"' }, { id: "push", label: "Push", command: "git push" });
  return { ...settings, commands };
}

/** The tag a deploy moves onto what it put live; the git panel draws it on that commit. */
export const DEPLOY_TAG = "deployed";

/**
 * Every deploy also leaves a tag that is never moved, named for when it went
 * out (UTC): `deploy/2026-09-24-143205`. They are the history `DEPLOY_TAG`
 * forgets. Not `deployed/…`: a ref cannot be both a name and a directory.
 */
export const DEPLOY_HISTORY_PREFIX = "deploy/";
const DEPLOY_HISTORY_TAG = /^deploy\/(\d{4})-(\d{2})-(\d{2})-(\d{2})(\d{2})(\d{2})$/;

/** When a history tag's deploy went out, or null for any other tag name. */
export function deployedAt(tag: string): number | null {
  const m = DEPLOY_HISTORY_TAG.exec(tag);
  if (!m) return null;
  const [y, mo, d, h, mi, s] = m.slice(1).map(Number) as [number, number, number, number, number, number];
  return Date.UTC(y, mo - 1, d, h, mi, s);
}

/**
 * Where a worktree's work goes before it is deployed: "Merge into staging"
 * merges into it, and Deploy merges it into main. Made from what the checkout
 * has out the first time a worktree's button needs it.
 */
export const STAGING_BRANCH = "staging";

const HAS_STAGING = `git rev-parse -q --verify refs/heads/${STAGING_BRANCH} >/dev/null`;

/**
 * Why `name` cannot be a queued task's branch — the one auto-merge takes its
 * worktree into instead of staging — or null. Never the trunk: main moves only
 * on a Deploy. Never a worktree's own branch (`worktree-…`) or auto-merge's
 * scratch one: auto-merge moves the branch with `git branch -f`, which git
 * refuses for one a tree has out. Blank and `staging` itself are staging, and
 * stored as nothing (`mergeBranchOf`).
 */
export function mergeBranchProblem(name: string): string | null {
  const problem = branchNameProblem(name);
  if (problem) return problem;
  if (!/^[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(name)) return "letters, digits, . _ / - only";
  if (name === "main" || name === "master") return "not the trunk: it moves only on a Deploy";
  if (name.startsWith("worktree-") || name.startsWith("auto-merge/")) return "not a worktree's own branch";
  return null;
}

/** A branch as typed, for storing: blank or staging is null, which is staging. */
export const mergeBranchOf = (raw: string | null | undefined): string | null => {
  const name = (raw ?? "").trim();
  return !name || name === STAGING_BRANCH ? null : name;
};

/**
 * The branch graph's node standing for every Claude worktree: its one link
 * says where a worktree's work goes (staging, unless it names another), and
 * that link's auto is auto-merge. Not a name git takes, so no branch is it.
 */
export const WORKTREES_NODE = "*";
/** The trunk, as the branch settings name it: `main`, or `master` on a repo
 *  that has that and no main — like every "main" in the git panel. */
export const TRUNK_NODE = "main";
export const REPO_BRANCHES_MAX = 20;
export const REPO_MERGES_MAX = 60;

/** A branch the repo's settings know of. */
export interface RepoBranch {
  name: string;
  /** Pushed to the git server whenever the checkout has it ahead of origin's,
   *  never forced (server/autoPush.ts). Absent is off. */
  autoPush?: true;
  /** The git server's copy merged in whenever it has commits the branch lacks:
   *  the pills' Pull (server/autoPush.ts), so a conflict is auto-fix's like a
   *  Pull's by hand. Absent is off. */
  autoPull?: true;
  /** Deployed whenever it moves past what is live. The trunk's is the repo's
   *  Deploy (server/autoDeploy.ts, `RepoSettings.autoDeploy`); any other
   *  branch's is the box's `deploy-<repo>-<branch>` script, offered only where
   *  there is one (server/branchRuns.ts, `branchDeployCommand`). Absent is off. */
  autoDeploy?: true;
  /** Its tests run whenever it gets a new tip, in a throwaway tree
   *  (server/branchRuns.ts, `BRANCH_TEST`); with auto-fix on, a failure gets
   *  a Claude off the branch. Absent is off. */
  test?: true;
  /** "Start at review": a queued Claude made at this branch (or a queued prompt
   *  in a worktree going into it) that waits on a worktree or a task off the
   *  queue may start once that task is in `review` (what a merge into its target
   *  sets), not only once it is `done` (shared/tmux.ts `waitingOn`,
   *  `startsAtReview`). Absent is ON — only `false` is stored — since that is how
   *  it always worked. Not the trunk's: nothing is queued off it. */
  startAtReview?: false;
  /** Where this branch is served: whole http(s) URLs, a path allowed, as
   *  `normalizeBranchUrl` keeps them. Links only — nothing here routes. */
  urls?: string[];
}

export const REPO_BRANCH_URLS_MAX = 10;
export const REPO_BRANCH_URL_MAX = 500;

/** A URL typed for a branch, as stored — https:// when no scheme is given, no
 *  trailing slash on a bare host — or null when it is not an http(s) URL. */
export function normalizeBranchUrl(text: string): string | null {
  let t = text.trim();
  if (!t) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(t)) t = `https://${t}`;
  let url: URL;
  try {
    url = new URL(t);
  } catch {
    return null;
  }
  if ((url.protocol !== "https:" && url.protocol !== "http:") || !url.hostname || url.username || url.password) return null;
  const href = url.pathname === "/" && !url.search && !url.hash ? url.href.slice(0, -1) : url.href;
  return href.length > REPO_BRANCH_URL_MAX ? null : href;
}

/** A branch's URLs from anywhere untrusted, or what is wrong with them. */
function branchUrlsOf(name: string, raw: unknown): string[] | string {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) return `${name}: urls must be a list`;
  if (raw.length > REPO_BRANCH_URLS_MAX) return `${name}: at most ${REPO_BRANCH_URLS_MAX} URLs`;
  const out: string[] = [];
  for (const u of raw) {
    const url = typeof u === "string" ? normalizeBranchUrl(u) : null;
    if (!url) return `${name}: ${String(u).trim() || "a blank"} is not an http(s) URL`;
    if (!out.includes(url)) out.push(url);
  }
  return out;
}

/** Every branch URL the repo's settings list, with its branch, in branch order. */
export const branchUrls = (s: RepoSettings | undefined): { branch: string; url: string }[] =>
  branchesOf(s).branches.flatMap((b) => (b.urls ?? []).map((url) => ({ branch: b.name, url })));

/**
 * One direction of a link between two branches: `from` may be merged into
 * `into` — offered on their pills — and, with `auto`, is merged on its own
 * whenever `from` has commits `into` lacks (server/autoMerge.ts). `auto` is when
 * it was ticked, in ms: for the worktrees' link, work older than that is left
 * alone. Into the trunk only from staging, and its auto is "Auto deploy to MAIN":
 * main moves only on a Deploy.
 */
export interface RepoMerge {
  from: string;
  into: string;
  auto?: number;
}

export interface RepoBranchSet {
  branches: RepoBranch[];
  merges: RepoMerge[];
}

/** What the flags said before there were branches: main and staging, the
 *  worktrees into staging, and staging and main either way. */
export function legacyBranches(s: Pick<RepoSettings, "autoMerge" | "autoPush" | "autoDeployStaging" | "autoDeploy" | "startAtReview">): RepoBranchSet {
  const push = s.autoPush ? { autoPush: true as const } : {};
  const review = s.startAtReview === false ? { startAtReview: false as const } : {};
  return {
    branches: [
      { name: TRUNK_NODE, ...push, ...review, ...(s.autoDeploy ? { autoDeploy: true as const } : {}) },
      { name: STAGING_BRANCH, ...push, ...review },
    ],
    merges: [
      { from: WORKTREES_NODE, into: STAGING_BRANCH, ...(s.autoMerge ? { auto: s.autoMerge } : {}) },
      { from: STAGING_BRANCH, into: TRUNK_NODE, ...(s.autoDeployStaging ? { auto: 1 } : {}) },
      { from: TRUNK_NODE, into: STAGING_BRANCH },
    ],
  };
}

/** The repo's branches and links: as saved, or as the old flags had them. */
export const branchesOf = (s: RepoSettings | undefined): RepoBranchSet =>
  s?.branches && s.merges ? { branches: s.branches, merges: s.merges } : legacyBranches(s ?? {});

/** Where a Claude worktree's work goes (`WORKTREES_NODE`'s link), and since when
 *  it goes on its own (null: only by hand). Staging when no link says. */
export function worktreeTarget(s: RepoSettings | undefined): { into: string; since: number | null } {
  const link = branchesOf(s).merges.find((m) => m.from === WORKTREES_NODE);
  return { into: link?.into ?? STAGING_BRANCH, since: link?.auto ?? null };
}

/** The name a branch's deploy script carries after `deploy-<repo>-`: a `/` in
 *  the branch is a `-` there. */
export const branchScriptSlug = (branch: string): string => branch.replace(/\//g, "-");

/** The tag a branch's deploy moves onto what it put live, as `DEPLOY_TAG` is the trunk's. */
export const branchDeployTag = (branch: string): string => `${DEPLOY_TAG}-${branch}`;

/** Whether `branch` (the trunk as `TRUNK_NODE`) is pushed on its own. */
export const pushesBranch = (s: RepoSettings | undefined, branch: string): boolean =>
  branchesOf(s).branches.some((b) => b.name === branch && b.autoPush);

/** How auto-fix starts its Claudes; Manual is off. */
export const autoFixModeOf = (s: RepoSettings | undefined): StartMode => s?.autoFixMode ?? (s?.autoFix ? "ready" : "manual");

/** A repo's start limit: no more readily than Under, or than Wastage. */
/** The most a repo's "Max Claudes" takes: the Global settings' own most (`CLAUDE_LIMIT_MAX`). */
export const REPO_MAX_CLAUDES_MAX = 50;
export const isRepoMaxClaudes = (x: unknown): x is number => Number.isInteger(x) && (x as number) >= 1 && (x as number) <= REPO_MAX_CLAUDES_MAX;
/** A repo's "Max Claudes" (`RepoSettings.maxClaudes`), or null for no limit. */
export const maxClaudesOf = (s: RepoSettings | undefined): number | null => s?.maxClaudes ?? null;

export type RepoStartLimit = "under" | "wastage";
export const isRepoStartLimit = (x: unknown): x is RepoStartLimit => x === "under" || x === "wastage";

/** The repo's start limit as a start mode: Ready while there is none. */
export const startLimitOf = (s: RepoSettings | undefined): StartMode => s?.startLimit ?? "ready";

/** The limit as a row carries it (`repoMode`): null while there is none. */
export const repoModeOf = (s: RepoSettings | undefined): RepoStartLimit | null => s?.startLimit ?? null;

/** The settings with auto-fix on or off and the start limit as given (each
 *  left as it is when undefined), the derived `autoFixMode` written in step. */
export function withAutoFix(s: RepoSettings, p: { on?: boolean; limit?: RepoStartLimit | null }): RepoSettings {
  const on = p.on ?? autoFixModeOf(s) !== "manual";
  const limit = p.limit === undefined ? (s.startLimit ?? null) : p.limit;
  const { autoFix: _, autoFixMode: __, startLimit: ___, ...rest } = s;
  return {
    ...rest,
    ...(on ? { autoFixMode: limit ?? "ready", autoFix: true } : {}),
    ...(limit ? { startLimit: limit } : {}),
  };
}

/** The links in `set` with `from` → `into`'s auto ticked or not: ticked keeps
 *  when it first was, and a link that was not there is added. */
export function withMergeAuto(set: RepoBranchSet, from: string, into: string, on: boolean, now = Date.now()): RepoBranchSet {
  const had = set.merges.find((m) => m.from === from && m.into === into);
  const auto = on ? { auto: had?.auto ?? now } : {};
  const merges = had
    ? set.merges.map((m) => (m === had ? { from, into, ...auto } : m))
    : [...set.merges, { from, into, ...auto }];
  return { branches: set.branches, merges };
}

/**
 * The settings with the old flags set as the branches now hold them — for
 * whatever still writes one (a failed Deploy unticking "Auto deploy to MAIN",
 * the old routes): the link or branches they stand for, changed. `autoMerge`
 * ticks the worktrees' link, wherever it goes; `autoPush` main and staging;
 * `autoDeploy` the trunk's own.
 */
export function withAutos(s: RepoSettings, p: { autoMerge?: boolean; autoPush?: boolean; autoDeployStaging?: boolean; autoDeploy?: boolean }, now = Date.now()): RepoSettings {
  let set = branchesOf(s);
  if (p.autoDeploy !== undefined) {
    const on = p.autoDeploy;
    set = { merges: set.merges, branches: set.branches.map((b) => (b.name === TRUNK_NODE ? withFlag(b, "autoDeploy", on) : b)) };
    const { autoDeploy: _, ...rest } = s;
    s = on ? { ...rest, autoDeploy: true } : rest;
  }
  if (p.autoMerge !== undefined) set = withMergeAuto(set, WORKTREES_NODE, worktreeTarget(s).into, p.autoMerge, now);
  if (p.autoDeployStaging !== undefined) set = withMergeAuto(set, STAGING_BRANCH, TRUNK_NODE, p.autoDeployStaging, now);
  if (p.autoPush !== undefined) {
    const on = p.autoPush;
    set = {
      merges: set.merges,
      branches: set.branches.map((b) =>
        b.name === TRUNK_NODE || b.name === STAGING_BRANCH ? withPush(b, on) : b,
      ),
    };
  }
  return { ...s, branches: set.branches, merges: set.merges };
}

/** `b` with "Start at review" ticked or not, the rest of it kept: only off is stored. */
export function withStartAtReview(b: RepoBranch, on: boolean): RepoBranch {
  const { startAtReview: _, ...rest } = b;
  return on ? rest : { ...rest, startAtReview: false };
}

/** `b` with its auto-push ticked or not, the rest of it kept. */
export const withPush = (b: RepoBranch, on: boolean): RepoBranch => withFlag(b, "autoPush", on);

/** `b` with one of its boxes ticked or not, the rest of it kept. */
export function withFlag(b: RepoBranch, key: "autoPush" | "autoPull" | "autoDeploy" | "test", on: boolean): RepoBranch {
  const { [key]: _, ...rest } = b;
  return on ? { ...rest, [key]: true } : rest;
}

/** Why `name` cannot be one of a repo's branches, or null. Main (the trunk) and
 *  staging always are; anything else as a queued task's branch. */
export function repoBranchProblem(name: string): string | null {
  if (name === TRUNK_NODE || name === STAGING_BRANCH) return null;
  if (name.startsWith("origin/")) return "a local branch: not the git server's copy";
  return mergeBranchProblem(name);
}

/** Why `from` → `into` cannot be a link, or null (both known to be listed). */
export function repoMergeProblem(from: string, into: string): string | null {
  if (into === WORKTREES_NODE) return "nothing merges into the worktrees: that is their Catch up";
  if (from === into) return "a branch does not merge into itself";
  if (into === TRUNK_NODE && from !== STAGING_BRANCH) return `only ${STAGING_BRANCH} goes into main: main moves only on a Deploy`;
  return null;
}

/** A repo's branches and links as saved, or what is wrong with them. The trunk
 *  and staging are always there, first; a repeated branch or link counts once. */
export function parseBranchSet(rawBranches: unknown, rawMerges: unknown): RepoBranchSet | string {
  if (!Array.isArray(rawBranches) || !Array.isArray(rawMerges)) return "branches and merges must be lists";
  if (rawBranches.length > REPO_BRANCHES_MAX) return `at most ${REPO_BRANCHES_MAX} branches`;
  if (rawMerges.length > REPO_MERGES_MAX) return `at most ${REPO_MERGES_MAX} links`;
  const listed: RepoBranch[] = [];
  for (const b of rawBranches as { name?: unknown; autoPush?: unknown; autoPull?: unknown; autoDeploy?: unknown; test?: unknown; startAtReview?: unknown; urls?: unknown }[]) {
    const name = typeof b?.name === "string" ? b.name.trim() : "";
    if (!name) return "every branch needs a name";
    const problem = repoBranchProblem(name);
    if (problem) return `${name}: ${problem}`;
    if (listed.some((l) => l.name === name)) continue;
    const urls = branchUrlsOf(name, b.urls);
    if (typeof urls === "string") return urls;
    listed.push({
      name,
      ...(b.autoPush === true ? { autoPush: true as const } : {}),
      ...(b.autoPull === true ? { autoPull: true as const } : {}),
      ...(b.autoDeploy === true ? { autoDeploy: true as const } : {}),
      ...(b.test === true ? { test: true as const } : {}),
      ...(b.startAtReview === false ? { startAtReview: false as const } : {}),
      ...(urls.length ? { urls } : {}),
    });
  }
  const fixed = [TRUNK_NODE, STAGING_BRANCH].map((name) => listed.find((b) => b.name === name) ?? { name });
  const branches = [...fixed, ...listed.filter((b) => b.name !== TRUNK_NODE && b.name !== STAGING_BRANCH)];
  const names = new Set(branches.map((b) => b.name));
  const merges: RepoMerge[] = [];
  for (const m of rawMerges as { from?: unknown; into?: unknown; auto?: unknown }[]) {
    const from = typeof m?.from === "string" ? m.from.trim() : "";
    const into = typeof m?.into === "string" ? m.into.trim() : "";
    if (from !== WORKTREES_NODE && !names.has(from)) return `${from || "a link"}: not one of the repo's branches`;
    if (into !== WORKTREES_NODE && !names.has(into)) return `${into || "a link"}: not one of the repo's branches`;
    const problem = repoMergeProblem(from, into);
    if (problem) return problem;
    if (merges.some((l) => l.from === from && l.into === into)) continue;
    if (from === WORKTREES_NODE && merges.some((l) => l.from === WORKTREES_NODE)) return "the worktrees merge into one branch";
    merges.push({ from, into, ...(Number.isSafeInteger(m.auto) && (m.auto as number) > 0 ? { auto: m.auto as number } : {}) });
  }
  return { branches, merges };
}

/** Sets `$trunk` to the repo's trunk, as `trunkOf` (shared/gitGraph.ts) reads
 *  it off a graph: `main`, or `master` on a repo that has that and no main. */
const TRUNK =
  'trunk=main && { git rev-parse -q --verify refs/heads/main >/dev/null || ' +
  '! git rev-parse -q --verify refs/heads/master >/dev/null || trunk=master; }';

/**
 * What a merge (or auto-commit) that was refused over a CONFLICT exits with,
 * as `TmuxSession.ranStatus` then says: the merge would conflict, did conflict
 * and was aborted, or would carry unresolved conflict markers on. Any other
 * failure (a dirty tree git will not merge over, a push refused) is not one,
 * and is a person's. It is what auto-fix goes by (server/mergeFix.ts).
 */
export const MERGE_CONFLICT_STATUS = 79;

/** Reads a `git diff -U0` and prints, indented, each file it ADDS a conflict
 *  marker line to. Only added ones: a diff that takes markers out is the fix. */
const MARKED = "git diff -U0 --no-color --src-prefix=a/ --dst-prefix=b/";
const MARKED_FILES = `awk '/^\\+\\+\\+ /{f=substr($0,7)} /^\\+(<<<<<<<|>>>>>>>)( |$)/{if(!(f in s)){s[f]=1;print "  " f}}'`;

/**
 * `unmarked <into> <branch>`: refuses (`MERGE_CONFLICT_STATUS`) a branch whose
 * own commits — what it has since it left <into> — add conflict markers. Git
 * merges those as cleanly as any other line, so without this a half-resolved
 * merge somebody committed in a worktree goes on to staging, and main.
 */
const UNMARKED =
  `unmarked() { m=$(${MARKED} "$1...$2" | ${MARKED_FILES}); [ -z "$m" ] || { ` +
  'echo "$2 has unresolved conflict markers that $1 lacks:" >&2; echo "$m" >&2; echo "Nothing was merged." >&2; ' +
  `return ${MERGE_CONFLICT_STATUS}; }; }`;

/**
 * `merge_or_abort <git merge's arguments>`: a merge in the tree this runs in
 * that is ABORTED if it conflicts (`MERGE_CONFLICT_STATUS`), never left half
 * done. `TRIAL` has usually passed by now, but the branch may have moved
 * since, and a tree left mid-merge is one `git add -A` away from committing
 * its markers. A merge git refused to start (uncommitted work in its way) has
 * nothing to abort, and is a plain failure.
 */
const MERGE_OR_ABORT =
  'merge_or_abort() { git merge "$@" && return 0; git rev-parse -q --verify MERGE_HEAD >/dev/null || return 1; ' +
  'echo "The merge conflicts:" >&2; git diff --name-only --diff-filter=U | sed "s/^/  /" >&2; ' +
  `git merge --abort; echo "Aborted: nothing was merged." >&2; return ${MERGE_CONFLICT_STATUS}; }`;

/** Deploy's first step, in the checkout: staging into what it has out — not if
 *  it carries conflict markers, and aborted if it conflicts — then staging
 *  moved up to the result (a fast-forward, since it is now merged). */
const MERGE_STAGING =
  `{ ! ${HAS_STAGING} || { ${UNMARKED} && ${MERGE_OR_ABORT} && ` +
  `{ { unmarked HEAD ${STAGING_BRANCH} && merge_or_abort -q --no-edit ${STAGING_BRANCH}; } || { s=$?; echo "${STAGING_BRANCH} was not merged into $(git branch --show-current). Nothing was deployed." >&2; (exit $s); }; } && ` +
  `{ git branch -f ${STAGING_BRANCH} HEAD || echo "Could not bring ${STAGING_BRANCH} level with $(git branch --show-current)" >&2; }; }; }`;

/** `MERGE_STAGING` as it was before it refused conflict markers: a saved Deploy may still hold it. */
const OLD_MERGE_STAGING =
  `{ ! ${HAS_STAGING} || { ` +
  `{ git merge -q --no-edit ${STAGING_BRANCH} || { git merge --abort 2>/dev/null; echo "Merging ${STAGING_BRANCH} into $(git branch --show-current) would conflict. Nothing was deployed." >&2; false; }; } && ` +
  `{ git branch -f ${STAGING_BRANCH} HEAD || echo "Could not bring ${STAGING_BRANCH} level with $(git branch --show-current)" >&2; }; }; }`;

/** Whether a Deploy's command line takes staging into main (the filled one
 *  does; a saved one of somebody's own need not). */
export const mergesStaging = (command: string): boolean => command.includes(MERGE_STAGING) || command.includes(OLD_MERGE_STAGING);

/** The session option a Deploy stamps once `MERGE_STAGING` is through
 *  (`TmuxSession.stagingMerged`): from then on staging is the deploy's no
 *  longer, and a merge into it goes ahead (`deployHolding`). */
export const STAGING_MERGED_OPTION = "@todos_staged";

/** Stamps it. Run inside `RUN_WRAPPER` (runner/tmux.ts), whose `$3` is tmux;
 *  never fails the deploy, which then just holds staging to the end. */
export const MARK_STAGING_MERGED =
  `{ [ -z "$TMUX_PANE" ] || "$3" set-option -t "$TMUX_PANE" ${STAGING_MERGED_OPTION} 1 2>/dev/null; true; }`;

/**
 * What an empty Deploy runs when the box has a deploy script for the repo
 * (`/usr/local/sbin/deploy-<repo>`, which `claude` may sudo): push first,
 * because the script deploys what `main` on the git server holds. That commit
 * is read off the server before the script runs, as the script reads it, and
 * tagged only once the script succeeds. Pushing the tag fires post-receive, so
 * every git panel on the repo sees it move. The dated history tag goes up in
 * the same push, so the hook fires once.
 *
 * Before any of that it merges `STAGING_BRANCH` into main, which is where a
 * worktree's "Merge into staging" put the work — refused whole, and nothing
 * deployed, if it would conflict — then brings staging level with main, so the
 * next worktree made from main is not already "ahead" of it. Then it stamps its
 * session (`MARK_STAGING_MERGED`): staging is free to move on while the rest
 * of the deploy runs, and what lands on it goes out with the next one.
 */
export const deployCommand = (script: string): string =>
  deployLine(script, `${TRUNK} && git push && sha=$(git ls-remote origin "refs/heads/$trunk" | cut -f1)`);

/** The filled Deploy as it was before, which a Repos-view save may have kept
 *  and which is still the filled one: main-only, from before a repo's trunk
 *  could be `master`, either without `MARK_STAGING_MERGED`, and all of them
 *  from before `MERGE_STAGING` refused conflict markers. */
const oldDeployCommands = (script: string): string[] => {
  const mainOnly = "git push && sha=$(git ls-remote origin refs/heads/main | cut -f1)";
  const trunk = `${TRUNK} && git push && sha=$(git ls-remote origin "refs/heads/$trunk" | cut -f1)`;
  return [
    deployLine(script, mainOnly, true, OLD_MERGE_STAGING),
    deployLine(script, mainOnly, false, OLD_MERGE_STAGING),
    deployLine(script, trunk, false, OLD_MERGE_STAGING),
    deployLine(script, trunk, true, OLD_MERGE_STAGING),
  ];
};

const deployLine = (script: string, pushAndRead: string, marks = true, mergeStaging = MERGE_STAGING): string =>
  `${mergeStaging} && ${marks ? `${MARK_STAGING_MERGED} && ` : ""}${pushAndRead} && sudo -n ${script} && ` +
  `at=${DEPLOY_HISTORY_PREFIX}$(date -u +%Y-%m-%d-%H%M%S) && ` +
  `git tag -f ${DEPLOY_TAG} "$sha" && git tag -f "$at" "$sha" && ` +
  `git push -qf origin refs/tags/${DEPLOY_TAG} "refs/tags/$at"`;

/** A string as one single-quoted shell word. */
const shellWord = (text: string): string => `'${text.replace(/'/g, "'\\''")}'`;

/** A deploy script's path as the bridge reports it: nothing a shell reads specially. */
export const DEPLOY_SCRIPT_PATH = /^\/[A-Za-z0-9._/-]+$/;

/** `BRANCH_DEPLOY`'s id, and the `@todos_command` its sessions are stamped with. */
export const BRANCH_DEPLOY_ID = "deploy-branch";

/**
 * What deploys a branch other than the trunk (`$MESSAGE`), with the box's
 * `deploy-<repo>-<branch>` script (`script`): as the filled Deploy does for
 * main — the branch pushed first, never forced, because the script deploys what
 * the git server holds (not pushed when the server's copy already has it all);
 * that commit read off the server, and `branchDeployTag` moved onto it once the
 * script succeeds. Nothing is merged and no history tag is left. Run in the
 * checkout, so a Deploy of main holds it (`heldByDeploy`); it holds nothing.
 */
export const branchDeployCommand = (script: string): string =>
  '{ git check-ref-format "refs/heads/$MESSAGE" || { echo "Not a branch name: $MESSAGE" >&2; false; }; } && ' +
  'if git rev-parse -q --verify "refs/heads/$MESSAGE" >/dev/null && ! git merge-base --is-ancestor "refs/heads/$MESSAGE" "refs/remotes/origin/$MESSAGE" 2>/dev/null; then ' +
  'git push -q origin "refs/heads/$MESSAGE:refs/heads/$MESSAGE" || { echo "Could not push $MESSAGE: nothing was deployed" >&2; false; }; fi && ' +
  'sha=$(git ls-remote origin "refs/heads/$MESSAGE" | cut -f1) && { [ -n "$sha" ] || { echo "The git server has no $MESSAGE" >&2; false; }; } && ' +
  `echo "Deploying $MESSAGE at \${sha:0:12}" && sudo -n ${shellWord(script)} && ` +
  `git tag -f "${DEPLOY_TAG}-$MESSAGE" "$sha" && git push -qf origin "refs/tags/${DEPLOY_TAG}-$MESSAGE"`;

/**
 * Installs a new tree's `node_modules` (git never carries it) with whatever
 * the lockfile says, unless there is one already — the create hook, run first,
 * may have made it. `label` says who in the line it prints; a failure is only
 * said (`then` follows it), never fatal.
 */
export const installDeps = (label: string, then = ""): string =>
  `if [ -f package.json ] && [ ! -e node_modules ]; then printf "\\033[2m[${label}] installing node_modules\\033[0m\\n"; ` +
  "if [ -f pnpm-lock.yaml ]; then pnpm install --frozen-lockfile; elif [ -f yarn.lock ]; then yarn install --frozen-lockfile; " +
  "elif [ -f package-lock.json ]; then npm ci --no-audit --no-fund; else npm install --no-audit --no-fund; fi " +
  `|| echo "Installing node_modules failed ($?)${then}." >&2; fi; `;

/** Lowers the shell it runs in, and so all it starts, as test/nice.ts does `npm test`. */
const TEST_NICENESS = "renice -n 10 -p $BASHPID >/dev/null 2>&1; ionice -c 2 -n 7 -p $BASHPID >/dev/null 2>&1; ";

/**
 * The lock that lets only one test run go at a time on the box, whatever the
 * repo or user (claude's auto-tests and Claudes, todos's Deploy): a run waits
 * for it, silently. A DIRECTORY, so any user can open it: /tmp's
 * protected_regular refuses opening another user's FILE there to flock(1),
 * which always asks to create it. `$TODOS_TEST_LOCK_DIR` moves it (tests only).
 */
export const TEST_LOCK_DIR = "/tmp/todos-tests.lock";

/**
 * Set by whoever holds the lock for everything under it, which then does not
 * take it again: the test run it starts would otherwise wait on its own parent
 * (todos's `npm test` under an auto-test, test/repoSettings.test.ts under `npm test`).
 */
export const TEST_LOCKED_ENV = "TODOS_TEST_LOCKED";

/**
 * Takes the test lock on fd 9 for the rest of the (sub)shell, unless it is held
 * already. The work goes under `9<&-`, so nothing it leaves running keeps it.
 * No flock (macOS): unlocked.
 */
const TAKE_TEST_LOCK =
  `if [ -z "$${TEST_LOCKED_ENV}" ]; then l="\${TODOS_TEST_LOCK_DIR:-${TEST_LOCK_DIR}}"; mkdir -p "$l" 2>/dev/null; ` +
  `{ exec 9<"$l" && flock 9; } 2>/dev/null; export ${TEST_LOCKED_ENV}=1; fi; `;

/** `branchTestCommand`'s id, and the `@todos_command` its sessions are stamped with. */
export const BRANCH_TEST_ID = "test-branch";

/**
 * A branch's auto-test (`RepoBranch.test`): the repo's test command run on
 * `$COMMIT` (the branch's tip, `$MESSAGE` the branch) in a throwaway detached
 * tree, so neither the checkout nor any worktree is touched and a Claude
 * mid-turn is never in its way. The repo's worktree hooks run in it as in any
 * new worktree, a failing one only said; then its `node_modules` is installed
 * (`installDeps`) unless the hook made one — without it the tests cannot even
 * start (`tsx: not found`). All of it runs niced (`TEST_NICENESS`), CPU and
 * disk: it shares the box with todos and every Claude at work — and only once
 * no other test run is going, anywhere (`TAKE_TEST_LOCK`). The tree is
 * removed whatever the tests said; the command fails as they did.
 */
export function branchTestCommand(test: string, hooks: WorktreeHooks = {}): string {
  const env = 'export CHECKOUT WORKTREE; WORKTREE=$(basename "$t")';
  const create = hooks.create ? `{ ${hooks.create}
} || echo "The worktree's create hook failed ($?)" >&2; ` : "";
  const remove = hooks.remove ? `( cd "$t" && ${env} && { ${hooks.remove}
} ) || echo "The worktree's remove hook failed ($?)" >&2; ` : "";
  return (
    'CHECKOUT="$PWD"; t=$(mktemp -d) && git worktree add -q --detach "$t" "$COMMIT" && { ' +
    'echo "Testing $MESSAGE at $(git rev-parse --short "$COMMIT")"; ' +
    `( cd "$t" || exit 1; ${TEST_NICENESS}${TAKE_TEST_LOCK}{ ${env}; ${create}${installDeps("test")}${test}
} 9<&- ); r=$?; ` +
    `${remove}git worktree remove --force "$t"; ` +
    '[ $r = 0 ] && echo "The tests passed" || echo "The tests failed ($r)" >&2; (exit $r); }'
  );
}

/**
 * Whether a command waits while a Deploy of its repo runs (`runningDeploy`):
 * anything in the checkout, which has main out — a second Deploy, Commit, Push,
 * the commit menu's tag and Move main — and Merge into staging from a worktree,
 * since staging is what the Deploy merges: that only until the Deploy has
 * merged it (`deployHolding` in shared/tmux.ts).
 * The rest of a worktree's buttons touch only the worktree. The run route
 * refuses them, or with `queue` keeps them until it ends (server/runQueue.ts),
 * which is what the tmux view's buttons and the git panel's menus ask for.
 * A branch's auto-test is not held, nor holds anything: it runs in a throwaway
 * tree of its own, and only starts from the checkout.
 */
export const heldByDeploy = (commandId: string, worktree: string | null): boolean =>
  commandId !== BRANCH_TEST_ID && (!worktree || commandId === "merge-into" || commandId === AUTO_MERGE_ID);

/** `AUTO_MERGE`'s id, and the `@todos_command` its sessions are stamped with. */
export const AUTO_MERGE_ID = "auto-merge";

/**
 * The settings with an empty Deploy filled from the repo's deploy script.
 * `quick` (the script takes `--quick`) with `quickDeploy` ticked fills it with
 * the quick one — only where it is RUN: the Repos view is shown the plain one,
 * so a save there never freezes `--quick` in. A Deploy saved as the plain
 * filled line (a save there does keep it) turns quick the same way.
 */
export function withDeployScript(settings: RepoSettings, script: string | undefined, quick = false): RepoSettings {
  if (!script) return settings;
  const filled = deployCommand(script);
  const old = oldDeployCommands(script);
  const command = quick && settings.quickDeploy ? deployCommand(`${script} --quick`) : filled;
  return {
    ...settings,
    commands: settings.commands.map((c) => (c.id === "deploy" && (!c.command || c.command === filled || old.includes(c.command)) ? { ...c, command } : c)),
  };
}

/** The repo's own checkout and the branch it has out, from inside one of its
 *  worktrees: `git worktree list` always names the main working tree first. */
const MAIN_CHECKOUT =
  'main=$(git worktree list --porcelain | sed -n "1s/^worktree //p") && ' +
  'into=$(git -C "$main" branch --show-current) && ' +
  '{ [ -n "$into" ] || { echo "$main is not on a branch" >&2; false; }; }';

/**
 * `trial <onto> <branch>`: whether merging <branch> onto <onto> goes through
 * cleanly, worked out in a throwaway detached worktree so neither real tree is
 * touched — a conflict is reported (`MERGE_CONFLICT_STATUS`), never left
 * half-merged. (git on the box is too old for `merge-tree --write-tree`, which
 * would need no checkout at all.)
 */
const TRIAL =
  'trial() { t=$(mktemp -d) && git worktree add -q --detach "$t" "$1" || return 1; ' +
  `if git -C "$t" merge -q --no-commit --no-ff "$2" >/dev/null 2>&1; then r=0; else r=${MERGE_CONFLICT_STATUS}; ` +
  'echo "Merging $2 into $1 would conflict:" >&2; git -C "$t" diff --name-only --diff-filter=U | sed "s/^/  /" >&2; ' +
  'echo "Nothing was merged." >&2; fi; ' +
  'git worktree remove --force "$t"; return $r; }';

/**
 * `into_staging <branch> <message>`: merges <branch> into `STAGING_BRANCH` in a
 * throwaway DETACHED worktree at staging's tip, then moves staging onto the
 * result — only if it is still where it was read. Never `worktree add
 * staging`: git refuses that outright on a repo where some tree has staging
 * out (a staging site's checkout), and every merge into it then failed. Such a
 * tree is left as it is, as auto-merge leaves it: the branch moves, its files
 * do not.
 */
const INTO_STAGING =
  `into_staging() { old=$(git rev-parse --verify -q refs/heads/${STAGING_BRANCH}) && t=$(mktemp -d) && git worktree add -q --detach "$t" "$old" || return 1; ` +
  'if git -C "$t" merge -q --no-edit -m "$2" "$1"; then new=$(git -C "$t" rev-parse HEAD); r=0; else r=1; fi; ' +
  `git worktree remove --force "$t"; [ $r = 0 ] && git update-ref -m "merge $1" refs/heads/${STAGING_BRANCH} "$new" "$old"; }`;

/** Makes `STAGING_BRANCH` from what the checkout has out (`$into`) if the repo has none. */
const ENSURE_STAGING =
  `{ ${HAS_STAGING} || { git branch ${STAGING_BRANCH} "$into" && echo "Made ${STAGING_BRANCH} from $into"; }; }`;

/**
 * The buttons a session in a Claude worktree gets besides the repo's own:
 * fixed, never saved, because what they do is git's and not the repo's. Their
 * ids have a hyphen, which a saved command's never can (`COMMAND_ID`), so
 * neither can shadow the other. Both go by `STAGING_BRANCH`, made from what
 * the checkout has out (main on every repo here) if it is missing; Deploy is
 * what takes staging into main. A merge that would conflict is refused whole,
 * by `TRIAL`, and git itself refuses one that would overwrite uncommitted work;
 * one that conflicts all the same (the branch moved since the trial) is aborted
 * (`MERGE_OR_ABORT`). A branch that carries conflict markers somebody committed
 * is never merged into staging (`UNMARKED`).
 * Staging has no tree of its own, so the merge into it is made in a throwaway
 * one; the worktree is then fast-forwarded onto the result, so the two end
 * level.
 */
export const WORKTREE_COMMANDS: readonly RepoCommand[] = [
  {
    id: "merge-into",
    label: `Merge into ${STAGING_BRANCH}`,
    command:
      `${MAIN_CHECKOUT} && ${ENSURE_STAGING} && ${TRIAL} && ${UNMARKED} && ${INTO_STAGING} && b=$(git branch --show-current) && unmarked ${STAGING_BRANCH} "$b" && trial ${STAGING_BRANCH} "$b" && ` +
      `into_staging "$b" "Merge branch '$b' into ${STAGING_BRANCH}" && ` +
      `echo "Merged $b into ${STAGING_BRANCH}" && git merge -q --ff-only ${STAGING_BRANCH}`,
  },
  {
    id: "merge-from",
    label: `Catch up ${STAGING_BRANCH}`,
    command: `${MAIN_CHECKOUT} && ${ENSURE_STAGING} && ${TRIAL} && b=$(git branch --show-current) && trial "$b" ${STAGING_BRANCH} && ${MERGE_OR_ABORT} && merge_or_abort --no-edit ${STAGING_BRANCH}`,
  },
];

/**
 * The staging pill's menu, run in the checkout: staging into main, as Deploy's
 * first step does but without deploying, or main into staging. Hyphenated like
 * the rest, and accepted by the run route only with no worktree named. Both
 * are tried first (`TRIAL`) and refused whole if they would conflict; into
 * main is also refused while staging carries conflict markers (`UNMARKED`), and
 * aborted if it conflicts after all (`MERGE_OR_ABORT`). Into
 * main then brings staging level with it, as Deploy does; main is not pushed —
 * Push or Deploy does that. Staging has no tree, so from main merges in a
 * throwaway one. Push sends staging (or, from the main pill, the trunk) to the
 * git server, never forced: one that has moved on there is refused rather than
 * overwritten. Pull is the other way: the git server's copy fetched and merged
 * in (staging's in a throwaway tree), tried first and refused whole on a
 * conflict — which auto-fix then answers (server/mergeFix.ts, `PULL_IDS`).
 */
/** `fetch_origin <branch>`: the git server's <branch> into `origin/<branch>`, as
 *  a Pull's first step — forced, as `git fetch` moves a remote-tracking branch. */
const FETCH_ORIGIN =
  'fetch_origin() { git fetch -q origin "+refs/heads/$1:refs/remotes/origin/$1" || { echo "Could not fetch $1 from the git server" >&2; return 1; }; }';
const NEEDS_STAGING = `{ ${HAS_STAGING} || { echo "There is no ${STAGING_BRANCH} branch" >&2; false; }; }`;
export const STAGING_COMMANDS: readonly RepoCommand[] = [
  {
    id: "staging-into-main",
    label: `Merge ${STAGING_BRANCH} into main`,
    command:
      `${NEEDS_STAGING} && ${TRUNK} && ` +
      '{ [ "$(git branch --show-current)" = "$trunk" ] || { echo "The checkout has $(git branch --show-current) out, not $trunk" >&2; false; }; } && ' +
      `${TRIAL} && ${UNMARKED} && ${MERGE_OR_ABORT} && unmarked "$trunk" ${STAGING_BRANCH} && trial "$trunk" ${STAGING_BRANCH} && ` +
      `merge_or_abort -q --no-edit ${STAGING_BRANCH} && echo "Merged ${STAGING_BRANCH} into $trunk" && ` +
      `{ git branch -f ${STAGING_BRANCH} HEAD || echo "Could not bring ${STAGING_BRANCH} level with $trunk" >&2; }`,
  },
  {
    id: "main-into-staging",
    label: `Merge main into ${STAGING_BRANCH}`,
    command:
      `${NEEDS_STAGING} && ${TRUNK} && ${TRIAL} && ${INTO_STAGING} && trial ${STAGING_BRANCH} "$trunk" && ` +
      `into_staging "$trunk" "Merge branch '$trunk' into ${STAGING_BRANCH}" && ` +
      `echo "Merged $trunk into ${STAGING_BRANCH}"`,
  },
  {
    id: "push-staging",
    label: `Push ${STAGING_BRANCH}`,
    command: `${NEEDS_STAGING} && git push -q origin refs/heads/${STAGING_BRANCH}:refs/heads/${STAGING_BRANCH} && echo "Pushed ${STAGING_BRANCH}"`,
  },
  {
    id: "push-main",
    label: "Push main",
    command: `${TRUNK} && git push -q origin "refs/heads/$trunk:refs/heads/$trunk" && echo "Pushed $trunk"`,
  },
  {
    id: "pull-main",
    label: "Pull main",
    command:
      `${TRUNK} && ` +
      '{ [ "$(git branch --show-current)" = "$trunk" ] || { echo "The checkout has $(git branch --show-current) out, not $trunk" >&2; false; }; } && ' +
      `${FETCH_ORIGIN} && fetch_origin "$trunk" && ${TRIAL} && ${UNMARKED} && ${MERGE_OR_ABORT} && ` +
      'if git merge-base --is-ancestor "refs/remotes/origin/$trunk" HEAD; then echo "$trunk already has origin/$trunk"; ' +
      'else unmarked "$trunk" "refs/remotes/origin/$trunk" && trial "$trunk" "refs/remotes/origin/$trunk" && ' +
      'merge_or_abort -q --no-edit "refs/remotes/origin/$trunk" && echo "Pulled origin/$trunk into $trunk"; fi',
  },
  {
    id: "pull-staging",
    label: `Pull ${STAGING_BRANCH}`,
    command:
      `${NEEDS_STAGING} && ${FETCH_ORIGIN} && fetch_origin ${STAGING_BRANCH} && ${TRIAL} && ${UNMARKED} && ${INTO_STAGING} && ` +
      `if git merge-base --is-ancestor refs/remotes/origin/${STAGING_BRANCH} refs/heads/${STAGING_BRANCH}; then echo "${STAGING_BRANCH} already has origin/${STAGING_BRANCH}"; ` +
      `else unmarked ${STAGING_BRANCH} refs/remotes/origin/${STAGING_BRANCH} && trial ${STAGING_BRANCH} refs/remotes/origin/${STAGING_BRANCH} && ` +
      `into_staging refs/remotes/origin/${STAGING_BRANCH} "Merge remote-tracking branch 'origin/${STAGING_BRANCH}' into ${STAGING_BRANCH}" && ` +
      `echo "Pulled origin/${STAGING_BRANCH} into ${STAGING_BRANCH}"; fi`,
  },
];

/** The Pulls' ids (`STAGING_COMMANDS`' two, the commit menu's for any other
 *  branch): a conflict of one is auto-fix's (server/mergeFix.ts). */
export const PULL_IDS: ReadonlySet<string> = new Set(["pull-main", "pull-staging", "pull-branch"]);

/** What auto-fix asks the Claude it starts off <branch> when the git server's
 *  copy of it would not merge in (a Pull refused over a conflict). */
export const pullConflictPrompt = (branch: string, remote = `origin/${branch}`): string =>
  `The git server's copy of ${branch} (${remote}) could not be pulled into it: they conflict, or ${remote} has unresolved conflict markers committed. ` +
  `Merge ${remote} into this branch (made from ${branch}), resolve the conflicts (and any conflict markers left in files) keeping both sides' intent, run the tests, and commit.`;

/**
 * `into_branch <branch> <from> <message>`: `INTO_STAGING` for any branch —
 * <from> merged into <branch> in a throwaway detached worktree, and <branch>
 * moved onto the result only if it is still where it was read. A <branch> not
 * there yet is made at <from>.
 */
const INTO_BRANCH =
  'into_branch() { old=$(git rev-parse --verify -q "refs/heads/$1") || { git branch "$1" "$2" && echo "Made $1 from $2"; return; }; ' +
  't=$(mktemp -d) && git worktree add -q --detach "$t" "$old" || return 1; ' +
  'if git -C "$t" merge -q --no-edit -m "$3" "$2"; then new=$(git -C "$t" rev-parse HEAD); r=0; else r=1; fi; ' +
  'git worktree remove --force "$t"; [ $r = 0 ] && git update-ref -m "merge $2" "refs/heads/$1" "$new" "$old"; }';

/** `MERGE_LINK`'s id: what the run route and auto-merge know it by. */
export const MERGE_LINK_ID = "merge-link";

/**
 * A link of the repo's branch settings merged, one way (`RepoMerge`): by a
 * pill's menu, or by auto-merge when the link's auto is ticked. `$MESSAGE` is
 * `<from> <into>` (branch names hold no space), `main` standing for the trunk,
 * and the run route takes only a pair the settings link. With `$COMMIT` set,
 * only while <from> is still on it. Never into the trunk — that is Deploy's, or
 * the staging pill's Merge into main — nor into the branch the checkout has
 * out, whose files would be left behind. Run in the checkout, tried first and
 * refused whole on a conflict or committed markers, like every merge here.
 */
export const MERGE_LINK: RepoCommand = {
  id: MERGE_LINK_ID,
  label: "Merge",
  command:
    `${TRUNK} && from="\${MESSAGE%% *}" && into="\${MESSAGE#* }" && ` +
    '{ [ "$from" != main ] || from=$trunk; } && { [ "$into" != main ] || into=$trunk; } && ' +
    '{ [ "$into" != "$trunk" ] || { echo "$trunk moves only on a Deploy" >&2; false; }; } && ' +
    '{ [ "$(git branch --show-current)" != "$into" ] || { echo "The checkout has $into out" >&2; false; }; } && ' +
    '{ git show-ref -q --verify "refs/heads/$from" || { echo "No branch $from" >&2; false; }; } && ' +
    '{ [ -z "${COMMIT:-}" ] || [ "$(git rev-parse "refs/heads/$from")" = "$COMMIT" ] || { echo "$from is not on $COMMIT any more" >&2; false; }; } && ' +
    `${TRIAL} && ${UNMARKED} && ${INTO_BRANCH} && ` +
    '{ ! git show-ref -q --verify "refs/heads/$into" || { unmarked "refs/heads/$into" "refs/heads/$from" && trial "refs/heads/$into" "refs/heads/$from"; }; } && ' +
    `into_branch "$into" "refs/heads/$from" "Merge branch '$from' into $into" && echo "Merged $from into $into"`,
};

/** A link's `$MESSAGE` for `MERGE_LINK`. */
export const linkMessage = (from: string, into: string): string => `${from} ${into}`;

/** A link's `$MESSAGE` back as its two branches, or null for anything else. */
export function linkOfMessage(message: string): { from: string; into: string } | null {
  const m = /^(\S+) (\S+)$/.exec(message);
  return m ? { from: m[1]!, into: m[2]! } : null;
}

/** What auto-fix asks the Claude it starts off <into> when <from> would not
 *  merge into it (`MERGE_LINK` refused over a conflict). */
export const linkConflictPrompt = (from: string, into: string): string =>
  `${from} could not be merged into ${into}: they conflict, or ${from} has unresolved conflict markers committed. ` +
  `Merge ${from} into this branch (made from ${into}), resolve the conflicts (and any conflict markers left in files) keeping both sides' intent, run the tests, and commit.`;

/**
 * What server/autoMerge.ts runs in a worktree when a commit lands on it: its
 * branch, as of `$COMMIT` (`onCommit`), merged into `STAGING_BRANCH` (made from
 * main if missing) by way of a branch of its own, `auto-merge/<branch>`, made
 * at staging in a throwaway worktree. Only once that merge has gone through
 * cleanly is staging moved onto it, a fast-forward; a conflict is reported and
 * nothing is merged, and neither is a commit that carries conflict markers
 * (`UNMARKED`). Main gets it with the next Deploy, as with the button.
 * `$MESSAGE`, when set, is the branch to merge into instead — a queued task's
 * (`mergeBranchProblem`), made from staging if missing — passed as an argument,
 * never written into the line.
 *
 * Unlike "Merge into staging" it never touches the worktree itself — the Claude
 * working there may be mid-turn, and committing again as this runs — so the
 * worktree does not end level with staging (Catch up staging does that). Never
 * a button, and in no list the run route reads: only the server starts it.
 */
export const AUTO_MERGE: RepoCommand = {
  id: AUTO_MERGE_ID,
  label: "Auto-merge",
  command:
    // A checkout previewing a commit (detached) is no hold: this never touches its files.
    'main=$(git worktree list --porcelain | sed -n "1s/^worktree //p") && into=$(git -C "$main" branch --show-current) && ' +
    `${TRUNK} && into="\${into:-$trunk}" && ` +
    '{ [ "$into" = "$trunk" ] || { echo "$main has $into out, not $trunk" >&2; false; }; } && ' +
    'b=$(git branch --show-current) && { [ -n "$b" ] || { echo "the worktree is not on a branch" >&2; false; }; } && ' +
    `${ENSURE_STAGING} && dest="\${MESSAGE:-${STAGING_BRANCH}}" && ` +
    `{ git rev-parse -q --verify "refs/heads/$dest" >/dev/null || { git branch "$dest" ${STAGING_BRANCH} && echo "Made $dest from ${STAGING_BRANCH}"; }; } && ` +
    `${UNMARKED} && unmarked "refs/heads/$dest" "$COMMIT" && ` +
    'git worktree prune && tmp="auto-merge/$b" && t=$(mktemp -d) && git worktree add -q -B "$tmp" "$t" "refs/heads/$dest" && ' +
    `{ if git -C "$t" merge -q --no-edit -m "Merge branch '$b'" "$COMMIT" >/dev/null; then ` +
    'r=0; { git merge-base --is-ancestor "refs/heads/$dest" "$tmp" && git branch -f "$dest" "$tmp"; } && echo "Merged $b ($(git rev-parse --short "$COMMIT")) into $dest" || r=1; ' +
    'else r=1; echo "Merging $b into $dest would conflict:" >&2;' +
    ' git -C "$t" diff --name-only --diff-filter=U | sed "s/^/  /" >&2; ' +
    'echo "Nothing was merged." >&2; git -C "$t" merge --abort 2>/dev/null; fi; ' +
    'git worktree remove --force "$t"; git branch -q -D "$tmp"; [ "$r" -eq 0 ]; }',
};

/** `AUTO_COMMIT`'s id, and the `@todos_command` its sessions are stamped with. */
export const AUTO_COMMIT_ID = "auto-commit";

/** The commands whose CONFLICTS server/mergeFix.ts follows — in a worktree, and
 *  in the checkout — whether auto-fix answers them or not. */
export const CONFLICTS_IN_WORKTREE: ReadonlySet<string> = new Set(["merge-into", "merge-from", AUTO_COMMIT_ID]);
export const CONFLICTS_IN_CHECKOUT: ReadonlySet<string> = new Set(["staging-into-main", "main-into-staging", MERGE_LINK_ID, "pull-main", "pull-staging", "pull-branch"]);

/** Whether a session's command ended in a conflict server/mergeFix.ts closes
 *  once it has seen it: nothing else closes one, or it could close it first
 *  and the conflict would go unanswered. (A command the server ran for itself
 *  and did not tell it of, the prompt queue's catch up, closes its own.) */
export const conflictFollowed = (t: { command?: { id: string } | null; worktree?: string | null; ranStatus?: number | null }): boolean =>
  t.ranStatus === MERGE_CONFLICT_STATUS && !!t.command && (t.worktree ? CONFLICTS_IN_WORKTREE : CONFLICTS_IN_CHECKOUT).has(t.command.id);

/**
 * What server/autoCommit.ts runs in a worktree whose Claude has stopped with
 * changes left: everything, untracked files included, as Commit takes it,
 * under the task's title (`$MESSAGE`). Only the worktree moves, so no Deploy
 * holds it; auto-merge then takes the commit on to staging. Nothing left to
 * commit — the Claude committed it itself meanwhile — is not a failure.
 * A tree left mid-merge, or with conflict markers in what it would commit, is
 * refused untouched (`MERGE_CONFLICT_STATUS`): `git add -A` would mark every
 * conflict resolved and commit the markers, and auto-merge would take them on.
 * Never a button, and in no list the run route reads: only the server starts it.
 */
export const AUTO_COMMIT: RepoCommand = {
  id: AUTO_COMMIT_ID,
  label: "Auto-commit",
  command:
    `u=$(git diff --name-only --diff-filter=U | sed "s/^/  /"); [ -n "$u" ] || u=$(${MARKED} HEAD | ${MARKED_FILES}); ` +
    'if [ -n "$u" ]; then echo "Unresolved merge conflicts in:" >&2; echo "$u" >&2; echo "Nothing was committed." >&2; ' +
    `(exit ${MERGE_CONFLICT_STATUS}); ` +
    'else git add -A && if git diff --cached --quiet; then echo "Nothing to commit"; ' +
    'else git commit -q -m "$MESSAGE" && git log -1 --format="Committed %h %s"; fi; fi',
};

/** What auto-fix asks the Claude it starts after a failed Deploy: its ask, and
 *  the name auto-commit gives a fix worktree's commits. */
export const AUTO_FIX_PROMPT = "Fix the errors in the test suite";

/** The tags auto-fix's prompt is filled with, as the task brief's are. */
export const AUTO_FIX_TAGS = [{ tag: "failed", what: "what failed in the Deploy: the failed tests, else its last step's output" }] as const;

/** What follows the ask once the Deploy's output is known, so its Claude checks
 *  its fix against what failed and does not first run the whole suite over again
 *  to find out — the next Deploy runs all of it anyway. Optional (`[…]`): a Test
 *  from a pill, with nothing failed, gets the ask alone. */
const AUTO_FIX_FAILED =
  "[\n\nThe Deploy failed. What failed:\n\n```\n<failed>\n```\n\n" +
  "Fix those failures, and check the fix by rerunning only what failed (the failing test files, or the typecheck) — " +
  "not the whole test suite: the next Deploy runs all of it.]";

/** Auto-fix's template as it stands with no repo's own (`RepoPrompts.autoFix`). */
export const DEFAULT_AUTO_FIX_PROMPT = AUTO_FIX_PROMPT + AUTO_FIX_FAILED;

/** Auto-fix's prompt, filled with the failed Deploy's output (`deployFailure()`)
 *  the way the task brief is (`fillTemplate()`: `<failed>`, and `[…]` left out
 *  while it is empty). A repo's own that names no tag — written before it could,
 *  or a conflict's ask handed in its place — is taken as written, with the
 *  default's account of the failure after it. */
export function autoFixPrompt(failure: string | null, own?: string | null): string {
  let template = own || DEFAULT_AUTO_FIX_PROMPT;
  const tags = AUTO_FIX_TAGS.map((t) => t.tag);
  if (!namesTag(template, tags)) {
    if (!failure) return template;
    // Brackets with no tag inside are left as written, so its own stay as they are.
    template += AUTO_FIX_FAILED;
  }
  return fillTemplate(template, { failed: failure ?? "" });
}

/** Auto-fix's worktrees (and the pills' Test's) are `fix-1`, `fix-2`…. They
 *  are for no task, so auto-commit names their commits `AUTO_FIX_PROMPT`. */
export const FIX_WORKTREE_PREFIX = "fix-";
export const isFixWorktree = (name: string): boolean => /^fix-\d+$/.test(name);

/** What auto-merge types at a worktree's Claude when merging its branch into
 *  staging failed — most likely because another worktree's merge got there
 *  first and touched the same lines (server/autoMerge.ts). */
export const AUTO_MERGE_CONFLICT_PROMPT =
  `This branch could not be merged into ${STAGING_BRANCH}: it conflicts with what is there now, or has unresolved conflict markers committed. ` +
  `Merge ${STAGING_BRANCH} into this branch, resolve the conflicts (and any conflict markers left in files) keeping both sides' intent, run the tests, and commit.`;

/** What auto-fix asks the Claude it starts, in a new worktree off staging, when
 *  staging and the trunk would not merge (Deploy's first step, or the staging
 *  pill's merges) — instead of `AUTO_FIX_PROMPT`: no test failed. */
export const STAGING_CONFLICT_PROMPT =
  `${STAGING_BRANCH} and the trunk (main, or master where there is no main) could not be merged: they conflict, or ${STAGING_BRANCH} has unresolved conflict markers committed. ` +
  `Merge the trunk into this branch, resolve the conflicts (and any conflict markers left in files) keeping both sides' intent, run the tests, and commit.`;

/** What a worktree's Claude is asked when auto-commit refused its tree (`AUTO_COMMIT`). */
export const AUTO_COMMIT_CONFLICT_PROMPT =
  "Auto-commit did not commit this worktree: it has unresolved merge conflicts (a merge still under way, or conflict markers left in files). " +
  "Resolve them keeping both sides' intent, run the tests, and commit.";

/**
 * What a removed worktree leaves behind: a tag on the commit it ended on,
 * `worktree/<name>`, or `worktree/<name>-<UTC Y-m-d-HMS>` when that is taken
 * by other work. A worktree made again under its name (Spawn worktree, a
 * status set back to doing) carries on from its tag, so when it goes again
 * the tag MOVES up to where it ended rather than leaving a second one behind;
 * otherwise never moved. Local only, as the worktree was. The git panel hides
 * these until the box in its header is ticked, and always while a worktree of
 * that name is out.
 */
export const WORKTREE_TAG_PREFIX = "worktree/";

/** The worktree a `worktree/…` tag was left by, its date suffix dropped, or
 *  null for any other tag or a name that is not a worktree's. */
export function worktreeOfTag(tag: string): string | null {
  if (!tag.startsWith(WORKTREE_TAG_PREFIX)) return null;
  const name = tag.slice(WORKTREE_TAG_PREFIX.length).replace(/-\d{4}-\d{2}-\d{2}-\d{6}$/, "");
  return worktreeNameProblem(name) ? null : name;
}

/** When a dated `worktree/…` tag was left (its UTC suffix), 0 for an undated
 *  one — the first of its name, so older than any dated one — or null for any
 *  other tag. */
export function worktreeTagAt(tag: string): number | null {
  if (!worktreeOfTag(tag)) return null;
  const m = /-(\d{4})-(\d{2})-(\d{2})-(\d{2})(\d{2})(\d{2})$/.exec(tag);
  if (!m) return 0;
  const [y, mo, d, h, mi, s] = m.slice(1).map(Number) as [number, number, number, number, number, number];
  return Date.UTC(y, mo - 1, d, h, mi, s);
}

/**
 * The git panel's Delete, on a worktree's pill: removes the worktree, tags where
 * it ended (`WORKTREE_TAG_PREFIX`), then deletes its branch if that is merged
 * into what the checkout has out, or into `STAGING_BRANCH`. The run route first
 * kills every session working in the worktree — the Delete is the decision to
 * be done with it — so the lock the Claude there held is stale by the time
 * this unlocks it. Never forced — git refuses a worktree with uncommitted or
 * untracked work in it, and `branch -d` one that is not merged, which is then
 * only reported. Hyphenated like the merges, and like them accepted only with
 * a worktree named; it is not one of a session's buttons, since it takes the
 * ground from under that session.
 */
export const DELETE_WORKTREE: RepoCommand = {
  id: "delete-worktree",
  label: "Delete worktree",
  command: deleteWorktree(),
};

/**
 * The same, offered when a session is KILLED from the tmux list. Claude Code
 * locks the worktree it works in, and a kill gives it no chance to unlock, so
 * a plain remove would refuse. Unlike Delete it kills nothing, so the run
 * route takes it only once no session is left in that worktree: a lock a live
 * Claude holds is a real one. Still never forced, so uncommitted work and an
 * unmerged branch survive it.
 */
export const REMOVE_KILLED_WORKTREE: RepoCommand = {
  id: "remove-killed-worktree",
  label: "Remove worktree",
  command: deleteWorktree(),
};

function deleteWorktree(): string {
  return (
    'main=$(git worktree list --porcelain | sed -n "1s/^worktree //p") && wt=$(git rev-parse --show-toplevel) && ' +
    '{ [ "$wt" != "$main" ] || { echo "$wt is the checkout, not a worktree" >&2; false; }; } && ' +
    'b=$(git branch --show-current) && sha=$(git rev-parse HEAD) && cd "$main" && ' +
    '{ git worktree unlock "$wt" 2>/dev/null || true; } && ' +
    'git worktree remove "$wt" && echo "Removed $wt" && ' +
    `t=${WORKTREE_TAG_PREFIX}$(basename "$wt") && ` +
    // Its own tag from before it was made again is moved up (-f); anyone else's name gets a date.
    'f= && { ! git rev-parse -q --verify "refs/tags/$t" >/dev/null || ' +
    '{ git merge-base --is-ancestor "refs/tags/$t" "$sha" && f=-f; } || t="$t-$(date -u +%Y-%m-%d-%H%M%S)"; } && ' +
    // The commits made in it, off its branch's reflog before `branch -d` takes that
    // (`ownCommits`), kept in the tag's message (`WORKTREE_TAG_OWN`) with those of
    // the tag it moves up: what Revise plan reverts once the branch is gone.
    `o=$({ [ -z "$f" ] || git tag -l --format='%(contents)' "$t"; [ -z "$b" ] || git reflog show --format='%H %gs' "refs/heads/$b" -- 2>/dev/null | sed -nE 's/^([0-9a-f]{40}) commit( \\([a-z]+\\))?: .*/own \\1/p'; } | grep -E '^own [0-9a-f]{40}$' | sort -u) && ` +
    // Annotated only where git can sign it with a name; plain otherwise, as it was.
    '{ { { git tag $f -m "worktree $(basename "$wt")$(printf "\\n\\n%s" "$o")" "$t" "$sha" 2>/dev/null || git tag $f "$t" "$sha"; } && echo "Tagged $(git rev-parse --short "$sha") $t"; } || echo "Could not tag $sha" >&2; } && ' +
    '{ [ -z "$b" ] || git branch -d "$b" 2>/dev/null || ' +
    `{ git merge-base --is-ancestor "$b" ${STAGING_BRANCH} 2>/dev/null && git branch -D "$b"; } || ` +
    'echo "Kept the branch $b: it is not merged" >&2; }'
  );
}

/** A line of a `worktree/…` tag's message naming a commit made in that worktree
 *  (`deleteWorktree`): Revise plan's record of the work, once its branch is gone. */
export const WORKTREE_TAG_OWN = /^own ([0-9a-f]{40})$/gm;

/** Refuses a checkout with uncommitted changes to tracked files: Preview and
 *  Back to main would carry them onto another commit, or stop half way. */
const CHECKOUT_CLEAN =
  '{ git diff --quiet HEAD || { echo "The checkout has uncommitted changes: commit them first" >&2; false; }; }';

/** After a checkout moved from `$from` to HEAD: `npm ci` when package-lock.json
 *  differs between them, so what runs off the checkout starts. */
const DEPS_SINCE =
  '{ [ ! -f package-lock.json ] || git diff --quiet "$from" HEAD -- package-lock.json || ' +
  '{ echo "package-lock.json differs: npm ci" && npm ci --no-audit --no-fund; }; }';

/** A commit as the git panel names it to the run route: a hash, never a ref name. */
export const COMMIT_HASH = /^[0-9a-f]{7,64}$/;

/**
 * What the git panel's commit menu can run on one commit, in the checkout:
 * fixed, never saved, and hyphenated like `WORKTREE_COMMANDS`. The run route
 * accepts them only with a commit named and no worktree, and puts the hash in
 * front as `COMMIT` (`onCommit`) — safe to write into the line because
 * `COMMIT_HASH` lets through nothing but hex.
 *
 * Add tag takes its name as `$MESSAGE`, refuses one git would not take or one
 * that already exists (never `-f`: moving a tag is Deploy's, for its own), and
 * pushes it so every git panel on the repo sees it. Move main is local only:
 * a push of main that moved back would have to be forced, and that is a
 * decision for Push or Deploy, not a menu. It says where main was, so the move
 * can be undone. `reset --keep` where main is out keeps uncommitted work, and
 * refuses when the move would touch a file that has some.
 *
 * Create branch takes its name as `$MESSAGE` too and, like Add tag, never
 * moves one that exists. Unlike it, the branch stays local: nothing is pushed.
 *
 * Push branch takes a local branch as `$MESSAGE`, only while it is still on
 * `$COMMIT`, and pushes it to the git server under its own name — never
 * forced, so a branch the server has moved on is refused. Never the trunk or
 * staging, whose pills have their own Push.
 *
 * Delete tag takes the tag as `$MESSAGE` too, and deletes it only while it is
 * still on `$COMMIT` (the menu may be stale), here and on the git server if it
 * is there — a `worktree/…` tag never was. `deployed` is Deploy's, not a
 * menu's: without it auto-deploy would take main as never shipped.
 *
 * Delete branch takes the branch as `$MESSAGE`, only while it is still on
 * `$COMMIT`: a local one is `branch -D`'d (the menu's second click is the
 * check, and it says how to put it back), an `origin/…` one is deleted on the
 * git server. Never the trunk or staging; a branch a tree has out is a
 * worktree pill, and git refuses it anyway.
 *
 * Pull branch takes the branch as `$MESSAGE` too, only while it is still on
 * `$COMMIT` and no tree has it out: the git server's copy fetched and merged
 * into it as the staging pill's Pull does, in a throwaway tree.
 *
 * Merge branch takes the branch as `$MESSAGE` too — a local one by name, the
 * git server's as `origin/…` (`origin/main` above all: what was pushed from
 * elsewhere) — only while it is still on `$COMMIT`, and merges it into
 * `STAGING_BRANCH` as the staging pill's Merge from main does: tried first
 * (`TRIAL`) and refused whole if it would conflict, then in a throwaway tree,
 * since staging has none. Main gets it with the next Deploy.
 *
 * Preview here checks `$COMMIT` out in the checkout, DETACHED (a branch a
 * worktree has out is git's to refuse), so whatever runs off the checkout —
 * todos' own dev instance — runs that commit; Back to main, only while the
 * checkout is still on `$COMMIT`, puts the trunk back. Neither carries
 * uncommitted work across (`CHECKOUT_CLEAN`), and each reinstalls when
 * package-lock.json differs (`DEPS_SINCE`). Meanwhile Deploy holds
 * (`deployPlan`) and Deploy, Commit and Push refuse (`ON_A_BRANCH`);
 * auto-merge carries on, since it never touches the checkout's files.
 */
export const COMMIT_COMMANDS: readonly RepoCommand[] = [
  {
    id: "add-tag",
    label: "Add tag",
    command:
      '{ git check-ref-format "refs/tags/$MESSAGE" || { echo "Not a tag name: $MESSAGE" >&2; false; }; } && ' +
      'git tag "$MESSAGE" "$COMMIT" && echo "Tagged $(git rev-parse --short "$COMMIT") $MESSAGE" && ' +
      '{ ! git remote get-url origin >/dev/null 2>&1 || git push -q origin "refs/tags/$MESSAGE"; }',
  },
  {
    id: "create-branch",
    label: "Create branch",
    command:
      '{ { git check-ref-format "refs/heads/$MESSAGE" && [ "${MESSAGE#-}" = "$MESSAGE" ]; } || { echo "Not a branch name: $MESSAGE" >&2; false; }; } && ' +
      '{ ! git show-ref -q --verify "refs/heads/$MESSAGE" || { echo "There is already a branch $MESSAGE" >&2; false; }; } && ' +
      'git update-ref "refs/heads/$MESSAGE" "$(git rev-parse --verify "$COMMIT^{commit}")" "" && ' +
      'echo "Branched $MESSAGE at $(git rev-parse --short "$COMMIT"). Not pushed."',
  },
  {
    id: "push-branch",
    label: "Push branch",
    command:
      `${TRUNK} && ` +
      `{ case "$MESSAGE" in "$trunk"|${STAGING_BRANCH}|origin/*) echo "$MESSAGE is not pushed from this menu" >&2; false;; esac; } && ` +
      '{ git show-ref -q --verify "refs/heads/$MESSAGE" || { echo "No branch $MESSAGE" >&2; false; }; } && ' +
      '{ [ "$(git rev-parse "refs/heads/$MESSAGE")" = "$(git rev-parse -q --verify "$COMMIT^{commit}")" ] || { echo "$MESSAGE is not on $COMMIT any more" >&2; false; }; } && ' +
      'git push -q origin "refs/heads/$MESSAGE:refs/heads/$MESSAGE" && echo "Pushed $MESSAGE"',
  },
  {
    id: "pull-branch",
    label: "Pull branch",
    command:
      `${TRUNK} && ` +
      `{ case "$MESSAGE" in "$trunk"|${STAGING_BRANCH}|origin/*) echo "$MESSAGE is not pulled from this menu" >&2; false;; esac; } && ` +
      '{ git show-ref -q --verify "refs/heads/$MESSAGE" || { echo "No branch $MESSAGE" >&2; false; }; } && ' +
      '{ [ "$(git rev-parse "refs/heads/$MESSAGE")" = "$(git rev-parse -q --verify "$COMMIT^{commit}")" ] || { echo "$MESSAGE is not on $COMMIT any more" >&2; false; }; } && ' +
      '{ [ "$(git branch --show-current)" != "$MESSAGE" ] || { echo "The checkout has $MESSAGE out" >&2; false; }; } && ' +
      `${FETCH_ORIGIN} && fetch_origin "$MESSAGE" && ${TRIAL} && ${UNMARKED} && ${INTO_BRANCH} && ` +
      'if git merge-base --is-ancestor "refs/remotes/origin/$MESSAGE" "refs/heads/$MESSAGE"; then echo "$MESSAGE already has origin/$MESSAGE"; ' +
      'else unmarked "refs/heads/$MESSAGE" "refs/remotes/origin/$MESSAGE" && trial "refs/heads/$MESSAGE" "refs/remotes/origin/$MESSAGE" && ' +
      'into_branch "$MESSAGE" "refs/remotes/origin/$MESSAGE" "Merge remote-tracking branch \'origin/$MESSAGE\' into $MESSAGE" && echo "Pulled origin/$MESSAGE into $MESSAGE"; fi',
  },
  {
    id: "merge-branch",
    label: `Merge branch into ${STAGING_BRANCH}`,
    command:
      `${NEEDS_STAGING} && ` +
      'if git show-ref -q --verify "refs/heads/$MESSAGE"; then ref="refs/heads/$MESSAGE"; ' +
      'elif [ "${MESSAGE#origin/}" != "$MESSAGE" ] && git show-ref -q --verify "refs/remotes/$MESSAGE"; then ref="refs/remotes/$MESSAGE"; ' +
      'else echo "No branch $MESSAGE" >&2; false; fi && ' +
      '{ [ "$(git rev-parse "$ref")" = "$(git rev-parse -q --verify "$COMMIT^{commit}")" ] || { echo "$MESSAGE is not on $COMMIT any more" >&2; false; }; } && ' +
      `${TRIAL} && ${INTO_STAGING} && trial ${STAGING_BRANCH} "$ref" && ` +
      `{ [ "$ref" = "refs/heads/$MESSAGE" ] && kind=branch || kind="remote-tracking branch"; } && ` +
      `into_staging "$ref" "Merge $kind '$MESSAGE' into ${STAGING_BRANCH}" && ` +
      `echo "Merged $MESSAGE into ${STAGING_BRANCH}"`,
  },
  {
    id: "delete-tag",
    label: "Delete tag",
    command:
      `{ [ "$MESSAGE" != ${DEPLOY_TAG} ] || { echo "${DEPLOY_TAG} is moved by Deploy, not deleted" >&2; false; }; } && ` +
      '{ [ "$(git rev-parse -q --verify "refs/tags/$MESSAGE^{commit}")" = "$(git rev-parse -q --verify "$COMMIT^{commit}")" ] || ' +
      '{ echo "No tag $MESSAGE on $COMMIT" >&2; false; }; } && ' +
      'git tag -d "$MESSAGE" && ' +
      '{ ! git remote get-url origin >/dev/null 2>&1 || ! git ls-remote --exit-code -q --tags origin "refs/tags/$MESSAGE" >/dev/null || ' +
      'git push -q origin ":refs/tags/$MESSAGE"; }',
  },
  {
    id: "delete-branch",
    label: "Delete branch",
    command:
      `${TRUNK} && ` +
      `{ case "$MESSAGE" in "$trunk"|"origin/$trunk"|${STAGING_BRANCH}|origin/${STAGING_BRANCH}) echo "$MESSAGE is not deleted from a menu" >&2; false;; esac; } && ` +
      'want=$(git rev-parse -q --verify "$COMMIT^{commit}") && ' +
      'if git show-ref -q --verify "refs/heads/$MESSAGE"; then ' +
      '{ [ "$(git rev-parse "refs/heads/$MESSAGE")" = "$want" ] || { echo "$MESSAGE is not on $COMMIT any more" >&2; false; }; } && ' +
      'git branch -q -D "$MESSAGE" && echo "Deleted $MESSAGE (was $(git rev-parse --short "$want")). To put it back: git branch $MESSAGE $want"; ' +
      'elif [ "${MESSAGE#origin/}" != "$MESSAGE" ] && git show-ref -q --verify "refs/remotes/$MESSAGE"; then ' +
      '{ [ "$(git rev-parse "refs/remotes/$MESSAGE")" = "$want" ] || { echo "$MESSAGE is not on $COMMIT any more" >&2; false; }; } && ' +
      'git push -q origin ":refs/heads/${MESSAGE#origin/}" && echo "Deleted ${MESSAGE#origin/} on the git server (was $(git rev-parse --short "$want")). To put it back: git push origin $want:refs/heads/${MESSAGE#origin/}"; ' +
      'else echo "No branch $MESSAGE" >&2; false; fi',
  },
  {
    id: "move-main",
    label: "Move main here",
    command:
      `${TRUNK} && ` +
      'old=$(git rev-parse --short "$trunk") && ' +
      'if [ "$(git branch --show-current)" = "$trunk" ]; then git reset -q --keep "$COMMIT"; else git branch -f "$trunk" "$COMMIT"; fi && ' +
      'echo "$trunk: $old -> $(git rev-parse --short "$trunk"). Not pushed. To put it back: git branch -f $trunk $old (git reset --keep $old where $trunk is out)"',
  },
  {
    id: "preview",
    label: "Preview here",
    command:
      `${TRUNK} && ${CHECKOUT_CLEAN} && from=$(git rev-parse HEAD) && git checkout -q --detach "$COMMIT" && ${DEPS_SINCE} && ` +
      'echo "Previewing $(git rev-parse --short HEAD). Deploy, Commit and Push wait until Back to $trunk"',
  },
  {
    id: "back-to-main",
    label: "Back to main",
    command:
      `${TRUNK} && ` +
      '{ { [ -z "$(git branch --show-current)" ] && [ "$(git rev-parse HEAD)" = "$(git rev-parse -q --verify "$COMMIT^{commit}")" ]; } || ' +
      '{ echo "The checkout is not previewing $COMMIT any more" >&2; false; }; } && ' +
      `${CHECKOUT_CLEAN} && from=$(git rev-parse HEAD) && git checkout -q "$trunk" && ${DEPS_SINCE} && ` +
      'echo "Back on $trunk ($(git rev-parse --short HEAD))"',
  },
];

/**
 * In front of what must not run in a checkout that is previewing a commit
 * (detached: Preview here) — a Deploy would merge staging into the preview and
 * move staging there, a Commit would leave its commit on no branch, a Push has
 * nothing to push. The run route puts it on the checkout's Deploy, Commit and
 * Push (`previewGuarded`); Build goes ahead, since building the preview is the point.
 */
export const ON_A_BRANCH =
  '{ git symbolic-ref -q HEAD >/dev/null || ' +
  '{ echo "The checkout is previewing $(git rev-parse --short HEAD): Back to main first" >&2; false; }; }';

/** The checkout's commands `ON_A_BRANCH` goes in front of. */
export const previewGuarded = (id: string): boolean => id === "deploy" || id === QUICK_DEPLOY_ID || id === "commit" || id === "push";

/** A commit command's line, for `hash` (already checked against `COMMIT_HASH`). */
export const onCommit = (command: string, hash: string): string => `COMMIT=${hash} && ${command}`;

/**
 * Whether running a command asks for a message first: it does when the
 * command reads `$MESSAGE`. Read off the command rather than a separate
 * switch, so the two can never disagree.
 */
export const needsMessage = (command: string): boolean => /\$\{?MESSAGE\b/.test(command);

const COMMAND_ID = /^[a-z0-9]{1,16}$/;
const DOMAIN = /^(?=.{1,253}(:|$))[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*(?::\d{1,5})?$/;

/** What someone typed as a domain, as stored: no scheme, path or case. */
export function normalizeDomain(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/\.$/, "");
}

export const newCommandId = (): string => Math.random().toString(36).slice(2, 10) || "c";

function isAutoDeployResume(raw: unknown): raw is AutoDeployResume {
  const r = raw as Partial<AutoDeployResume> | null | undefined;
  return (
    typeof r?.worktree === "string" &&
    !worktreeNameProblem(r.worktree) &&
    typeof r.main === "boolean" &&
    typeof r.staging === "boolean"
  );
}

/** Settings from anywhere untrusted: the settings, or what is wrong with them. */
export function parseRepoSettings(raw: unknown): RepoSettings | string {
  const o = (raw ?? {}) as { commands?: unknown; domains?: unknown; autoDeploy?: unknown; branches?: unknown; merges?: unknown; autoDeployStaging?: unknown; autoMerge?: unknown; autoCommit?: unknown; autoPush?: unknown; autoFixMode?: unknown; autoFix?: unknown; startLimit?: unknown; maxClaudes?: unknown; autoDeployResume?: unknown; startAtReview?: unknown; quickDeploy?: unknown; starred?: unknown; prompts?: unknown; worktreeHooks?: unknown; testCommand?: unknown };
  if (!Array.isArray(o.commands)) return "commands must be a list";
  if (!Array.isArray(o.domains)) return "domains must be a list";
  if (o.commands.length > REPO_COMMANDS_MAX) return `at most ${REPO_COMMANDS_MAX} commands`;
  if (o.domains.length > REPO_DOMAINS_MAX) return `at most ${REPO_DOMAINS_MAX} domains`;

  const commands: RepoCommand[] = [];
  const ids = new Set<string>();
  for (const c of o.commands as { id?: unknown; label?: unknown; command?: unknown }[]) {
    const label = typeof c?.label === "string" ? c.label.trim().replace(/\s+/g, " ") : "";
    const command = typeof c?.command === "string" ? c.command.trim() : "";
    if (!label) return "every command needs a name";
    if (label.length > REPO_COMMAND_LABEL_MAX) return `a command's name is at most ${REPO_COMMAND_LABEL_MAX} characters`;
    if (command.length > REPO_COMMAND_MAX) return `${label}: the command is too long`;
    if (command.includes("\0")) return `${label}: the command has a NUL in it`;
    // A missing or repeated id gets a fresh one rather than an error: it is
    // bookkeeping the person never sees.
    let id = typeof c?.id === "string" && COMMAND_ID.test(c.id) && !ids.has(c.id) ? c.id : "";
    while (!id || ids.has(id)) id = newCommandId();
    ids.add(id);
    commands.push({ id, label, command });
  }

  const domains: string[] = [];
  for (const d of o.domains) {
    const domain = typeof d === "string" ? normalizeDomain(d) : "";
    if (!domain) continue;
    if (!DOMAIN.test(domain)) return `${String(d).trim()} is not a domain`;
    if (!domains.includes(domain)) domains.push(domain);
  }
  const prompts = promptsOf(o.prompts);
  if (typeof prompts === "string") return prompts;
  const worktreeHooks = hooksOf(o.worktreeHooks);
  if (typeof worktreeHooks === "string") return worktreeHooks;
  const testCommand = typeof o.testCommand === "string" ? o.testCommand.replace(/\r\n/g, "\n").trim() : "";
  if (testCommand.length > REPO_COMMAND_MAX) return "the test command is too long";
  if (testCommand.includes("\0")) return "the test command has a NUL in it";
  // The branches, once written, are what the old flags are read off.
  const parsed = o.branches === undefined && o.merges === undefined ? null : parseBranchSet(o.branches, o.merges);
  if (typeof parsed === "string") return parsed;
  // The trunk's auto-deploy was the flag alone before the branches carried it,
  // and branches saved then lack it: the flag still says. Every save since
  // writes the two in step (`withAutos`), so either being on is on.
  // "Start at review" was the repo's before it was each branch's: off there is
  // off on every branch — and it is written off only while every branch is.
  const set = parsed && (o.autoDeploy === true || o.startAtReview === false)
    ? {
        merges: parsed.merges,
        branches: parsed.branches.map((b) => {
          const deployed = o.autoDeploy === true && b.name === TRUNK_NODE ? withFlag(b, "autoDeploy", true) : b;
          return o.startAtReview === false ? withStartAtReview(deployed, false) : deployed;
        }),
      }
    : parsed;
  const autoDeploy = set ? set.branches.some((b) => b.name === TRUNK_NODE && b.autoDeploy) : o.autoDeploy === true;
  const worktreesAuto = set?.merges.find((m) => m.from === WORKTREES_NODE)?.auto;
  const autoMerge = set ? worktreesAuto : Number.isSafeInteger(o.autoMerge) && (o.autoMerge as number) > 0 ? (o.autoMerge as number) : undefined;
  const autoPush = set ? set.branches.some((b) => b.autoPush) : o.autoPush === true;
  const autoPull = !!set?.branches.some((b) => b.autoPull);
  const autoDeployStaging = set ? set.merges.some((m) => m.from === STAGING_BRANCH && m.into === TRUNK_NODE && m.auto) : o.autoDeployStaging === true;
  const savedFixMode = isStartMode(o.autoFixMode) ? o.autoFixMode : o.autoFix === true ? "ready" : "manual";
  // Before the repo had a start limit, auto-fix's Under or Wastage was it.
  const startLimit = isRepoStartLimit(o.startLimit) ? o.startLimit : isRepoStartLimit(savedFixMode) ? savedFixMode : null;
  const autoFixMode: StartMode = savedFixMode === "manual" ? "manual" : (startLimit ?? "ready");
  // The trunk never has the box: nothing is queued off it (`mergeBranchProblem`).
  const reviewOff = set ? set.branches.every((b) => b.name === TRUNK_NODE || b.startAtReview === false) : o.startAtReview === false;
  return {
    commands,
    domains,
    ...(autoDeploy ? { autoDeploy: true } : {}),
    ...(set ? { branches: set.branches, merges: set.merges } : {}),
    ...(autoDeployStaging ? { autoDeployStaging: true } : {}),
    ...(autoMerge ? { autoMerge } : {}),
    ...(o.autoCommit === true ? { autoCommit: true } : {}),
    ...(autoPush ? { autoPush: true } : {}),
    ...(autoPull ? { autoPull: true } : {}),
    ...(autoFixMode !== "manual" ? { autoFixMode, autoFix: true } : {}),
    ...(startLimit ? { startLimit } : {}),
    ...(isRepoMaxClaudes(o.maxClaudes) ? { maxClaudes: o.maxClaudes } : {}),
    ...(isAutoDeployResume(o.autoDeployResume) ? { autoDeployResume: { worktree: o.autoDeployResume.worktree, main: o.autoDeployResume.main, staging: o.autoDeployResume.staging } } : {}),
    ...(reviewOff ? { startAtReview: false } : {}),
    ...(o.quickDeploy === true ? { quickDeploy: true } : {}),
    ...(starredOf(o.starred).length ? { starred: starredOf(o.starred) } : {}),
    ...(Object.keys(prompts).length ? { prompts } : {}),
    ...(Object.keys(worktreeHooks).length ? { worktreeHooks } : {}),
    ...(testCommand ? { testCommand } : {}),
  };
}

/** A repo's worktree hooks as saved: only the ones set, trimmed; blank is none. */
function hooksOf(raw: unknown): WorktreeHooks | string {
  if (raw === undefined || raw === null) return {};
  if (typeof raw !== "object" || Array.isArray(raw)) return "worktree hooks must be an object";
  const out: WorktreeHooks = {};
  for (const { key, label } of WORKTREE_HOOKS) {
    const v = (raw as Record<string, unknown>)[key];
    if (v === undefined || v === null) continue;
    if (typeof v !== "string") return `${label}: the hook must be text`;
    const text = v.replace(/\r\n/g, "\n").trim();
    if (text.length > REPO_COMMAND_MAX) return `${label}: the hook is too long`;
    if (text.includes("\0")) return `${label}: the hook has a NUL in it`;
    if (text) out[key] = text;
  }
  return out;
}

/** A repo's prompts as saved: only the ones set, trimmed at the ends only (the
 *  lines inside are the prompt); blank is the default. */
function promptsOf(raw: unknown): RepoPrompts | string {
  if (raw === undefined || raw === null) return {};
  if (typeof raw !== "object" || Array.isArray(raw)) return "prompts must be an object";
  const out: RepoPrompts = {};
  for (const { key, label } of REPO_PROMPTS) {
    const v = (raw as Record<string, unknown>)[key];
    if (v === undefined || v === null) continue;
    if (typeof v !== "string") return `${label}: the prompt must be text`;
    const text = v.replace(/\r\n/g, "\n").trim();
    if (text.length > REPO_PROMPT_MAX) return `${label}: the prompt is over ${REPO_PROMPT_MAX} characters`;
    if (text.includes("\0")) return `${label}: the prompt has a NUL in it`;
    if (text) out[key] = text;
  }
  return out;
}

/** A starred list as saved: worktree names only, each once, at most `STARRED_MAX`. */
function starredOf(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const names = raw.filter((n): n is string => typeof n === "string" && starrable(n));
  return [...new Set(names)].slice(0, STARRED_MAX);
}
